import { APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { ConfigService } from '../config/config.service';
import { AppStateService } from '../app-state/app-state.service';
import * as Sentry from '@sentry/angular';
import { ICustomerAppsConfig } from '../config/config.module';
import { Router } from '@angular/router';

export const SentryAppInitializerProvider = {
  provide: APP_INITIALIZER,
  useFactory: (configService: ConfigService<ICustomerAppsConfig>, appStateService: AppStateService) => () => {
    if (configService.config.sentryDsn && configService.config.sentryEnvironment) {
      Sentry.init({
        dsn: configService.config.sentryDsn,
        integrations: [
          // Registers and configures the Tracing integration,
          // which automatically instruments your application to monitor its
          // performance, including custom Angular routing instrumentation
          Sentry.browserTracingIntegration(),
          // Registers the Replay integration,
          // which automatically captures Session Replays
          Sentry.replayIntegration(),
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 0.05,
        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
        // Capture Replay for 0% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.05,
        environment: configService.config.sentryEnvironment,
        ignoreErrors: [/^Http failure response for (.*): 0 Unknown Error$/],
      });
      Sentry.setTag('runtime', appStateService.isNative ? 'wrapapp' : 'webapp');
      Sentry.setTag('webAppId', configService.config.appId);
      Sentry.setTag('webAppVersion', configService.config.appVersion);

      appStateService.nativeAppInfo$.subscribe((appInfo) => {
        Sentry.setTag('nativeAppNam', appInfo.name);
        Sentry.setTag('nativeAppId', appInfo.id);
        Sentry.setTag('nativeAppVersion', appInfo.version);
        Sentry.setTag('nativeAppBuild', appInfo.build);
      });
    }
  },
  deps: [ConfigService, AppStateService],
  multi: true,
};

export const SentryErrorHandlerProvider = {
  provide: ErrorHandler,
  useValue: Sentry.createErrorHandler({}),
};

export const SentryTraceServiceProvider = {
  provide: Sentry.TraceService,
  deps: [Router],
};

export const SentryProviders = [SentryErrorHandlerProvider, SentryTraceServiceProvider, SentryAppInitializerProvider];
